import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import PrivateRoute from './components/PrivateRoute';
import { FeatureFlagProvider } from './components/FeatureGate';
import AppErrorBoundary from './components/ErrorBoundary';
import FeatureRouteGuard from './components/FeatureRouteGuard';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import { FaBars, FaXmark } from "react-icons/fa6";
import { BookOpen } from "lucide-react";
import api from './api';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import UniDashboard from './pages/UniDashboard';
import PopDashboard from './pages/PopDashboard';
import PopLMSPage from './pages/PopLMSPage';
import AdminDashboard from './pages/AdminDashboard';
import StudentDashboard from './pages/StudentDashboard';
import FacultyDashboard from './pages/FacultyDashboard';
import TpoDashboard from './pages/TpoDashboard';
import HowItWorks from './pages/HowItWorks';
import DiscoverProfessors from './pages/DiscoverProfessors';
import DiscoverProfessorsUni from './pages/DiscoverProfessorsUni';
import VettingPage from './pages/VettingPage';
import ComplianceDashboard from './pages/ComplianceDashboard';
import AdminReportsPage from './pages/AdminReportsPage';
import AdminMessagesPage from './pages/AdminMessagesPage';
import GoogleCallback from './pages/GoogleCallBack';
import BrowseProfessorsPage from './pages/BrowseProfessorsPage';
import DiscussionsPage from './pages/DiscussionsPage';
import ProfessorProfileView from './pages/ProfessorProfileView';
import UniversityProfileView from './pages/UniversityProfileView';
import AiAgents from './pages/AiAgents';
import SidebarAiInterviewer from './pages/SidebarAiInterviewer';
import PublicJobPostingPage from './pages/PublicJobPostingPage';

import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import RefundPolicy from './pages/RefundPolicy';
import ForInstitutions from './pages/ForInstitutions';
import AboutUs from './pages/AboutUs';
import Pricing from './pages/Pricing';
import HelpCenter from './pages/HelpCenter';
import SafetyTrust from './pages/SafetyTrust';
import ContactUs from './pages/ContactUs';
import Careers from './pages/Careers';
import ForProfessionals from './pages/ForProfessionals';
import ChatPage from './pages/ChatPage';
import UserManualPage from './pages/UserManualPage';
import ServiceDeskPage from './pages/ServiceDeskPage';
import Footer from './components/Footer';
import './App.css';
import logo from './assets/edvergenceX.png';
import logo2 from './assets/edvergenceX2.png';
import { clearUIRulesCache } from './components/UIElementGuard';
import SessionManager from './components/SessionManager';

const WithFooter = ({ children }) => (
  <>
    {children}
    <Footer />
  </>
);

function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const role = localStorage.getItem('role');
  const baseRole = localStorage.getItem('base_role');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [unreadTicketResponses, setUnreadTicketResponses] = useState(0);

  const getRoleLabel = () => {
    const genericAdminRoles = ['admin', 'system_admin'];
    const displayRole = role === 'admin' && baseRole && !genericAdminRoles.includes(baseRole)
      ? baseRole
      : role;

    // A TPO account can also carry a custom role object whose name is what login stores
    // as `role` (e.g. "DEAN"), so the label falls back to the account's base role.
    if (displayRole === 'tpo' || baseRole === 'tpo') return 'TPO';
    if (displayRole === 'pop') return 'POP';
    if (displayRole === 'subadmin' || displayRole === 'sub_admin') return 'Sub-admin';
    return (displayRole || 'User')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, letter => letter.toUpperCase());
  };

  const fetchUnreadResponses = async () => {
    if (role && role !== 'admin') {
      try {
        const res = await api.get('/tickets/my-unread-responses-count');
        setUnreadTicketResponses(res.data.count || 0);
      } catch (err) {
        // silent catch
      }
    }
  };

  useEffect(() => {
    fetchUnreadResponses();
    const handleTicketUpdate = () => fetchUnreadResponses();
    window.addEventListener('ticket_read_status_updated', handleTicketUpdate);
    
    const interval = setInterval(fetchUnreadResponses, 60000); // Poll every minute
    return () => {
        clearInterval(interval);
        window.removeEventListener('ticket_read_status_updated', handleTicketUpdate);
    };
  }, [role, location.pathname]); // Refresh when navigating or role changes

  const handleLogout = () => {
    const keysToPreserve = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('seen_student_jobs')) {
        keysToPreserve.push({ key, value: localStorage.getItem(key) });
      }
    }
    localStorage.clear();
    keysToPreserve.forEach(item => localStorage.setItem(item.key, item.value));
    clearUIRulesCache();
    window.dispatchEvent(new Event('auth_changed'));
    navigate('/');
  };

  // Determine if we are on the home page
  const isHome = location.pathname === '/';

  const darkPaths = [
    '/', 
    '/browse-professors', 
    '/how-it-works',
    '/terms',
    '/privacy',
    '/refund-policy',
    '/institutions',
    '/about',
    '/pricing',
    '/help',
    '/safety',
    '/contact',
    '/careers',
    '/professionals'
  ];

  const isDarkPage = darkPaths.includes(location.pathname);

  useEffect(() => {
    // Force reset classes then apply the correct one
    document.body.classList.remove('light-theme');
    if (!isDarkPage) {
      document.body.classList.add('light-theme');
    }
  }, [isDarkPage, location.pathname]);

  const getDashboardPath = () => {
      switch (role) {
        case 'pop': return '/pop-dashboard';
        case 'university': return '/uni-dashboard';
        case 'student': return '/student-dashboard';
        case 'faculty': return '/faculty-dashboard';
        case 'tpo': return '/tpo-dashboard';
        case 'admin':
        case 'system_admin': return '/admin-dashboard';
        default: return '/';
      }
  };

  return (
    <nav className={`navbar ${isHome ? 'navbar-home' : 'navbar-solid'}`}>
      <Link to={role ? getDashboardPath() : "/"} className="nav-brand">
        {/* Logo on the left */}
        <img src={isDarkPage ? logo2 : logo} alt="EdVergenceX Logo" className="nav-logo" />
      </Link>

      <button className="mobile-menu-icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
        {isMobileMenuOpen ? <FaXmark /> : <FaBars />}
      </button>

      <div className={`nav-links ${isMobileMenuOpen ? 'nav-links-mobile' : ''}`} onClick={() => setIsMobileMenuOpen(false)}>


        {isHome && role && (
          <Link to={getDashboardPath()} className="nav-btn-outline" style={{ marginRight: '16px' }}>
            Dashboard
          </Link>
        )}
        {isHome && (
          <Link to="/how-it-works" className="nav-link-item" style={{ marginRight: '16px' }}>
            How It Works
          </Link>
        )}
        {role ? (
          <div className="auth-buttons">
            {/* <Link to="/discussions" className="nav-btn-outline" style={{ marginRight: '8px' }}>
              Discussions
            </Link> */}
            {role !== 'admin' && (
              <Link to="/service-desk" className="nav-link-item" style={{ display: 'flex', alignItems: 'center', gap: '6px', marginRight: '12px', position: 'relative' }} title="Service Desk">
                <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>Service Desk</span>
                {unreadTicketResponses > 0 && (
                  <span style={{ 
                    background: '#ef4444', color: 'white', borderRadius: '50%', padding: '2px 6px', 
                    fontSize: '0.7rem', fontWeight: 'bold', position: 'absolute', top: '-8px', right: '-12px' 
                  }}>
                    {unreadTicketResponses}
                  </span>
                )}
              </Link>
            )}
            {role !== 'custom_role' && localStorage.getItem('base_role') !== 'custom_role' && !role?.startsWith('custom_role') && (
              <Link to="/user-manual" className="nav-link-item" style={{ display: 'flex', alignItems: 'center', gap: '6px', marginRight: '12px' }} title="User Manual">
                <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>User Manual</span>
              </Link>
            )}
            <button onClick={handleLogout} className="nav-btn">
              Logout ({getRoleLabel()})
            </button>
          </div>
        ) : (
          <div className="auth-buttons">
            <Link to="/login" className="nav-btn">Login</Link>
            <Link to="/register" className="nav-btn-outline">Get Started</Link>
          </div>
        )}
      </div>
    </nav>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <Router>
    <AppErrorBoundary>
    <FeatureFlagProvider>
      <ScrollToTop />
      <SessionManager />
      <div className="app-container">
        {/* Navbar is now present for every single route */}
        <Navbar />

        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          {/* <Route path="/" element={<Home />} />    Homepage route. If required replace the above line with this  */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/auth/google/callback" element={<GoogleCallback />} />
          <Route path="/browse-professors" element={<BrowseProfessorsPage />} />
          <Route path="/post-job" element={<WithFooter><PublicJobPostingPage /></WithFooter>} />
          <Route path="/public/post-job" element={<WithFooter><PublicJobPostingPage /></WithFooter>} />
          <Route path="/how-it-works" element={<WithFooter><HowItWorks /></WithFooter>} />
          <Route path="/terms" element={<WithFooter><TermsOfService /></WithFooter>} />
          <Route path="/privacy" element={<WithFooter><PrivacyPolicy /></WithFooter>} /> {/* Will remove the inner footer in PrivacyPolicy to avoid double footer */}
          <Route path="/refund-policy" element={<WithFooter><RefundPolicy /></WithFooter>} />
          <Route path="/institutions" element={<WithFooter><ForInstitutions /></WithFooter>} />
          <Route path="/about" element={<WithFooter><AboutUs /></WithFooter>} />
          <Route path="/pricing" element={<WithFooter><Pricing /></WithFooter>} />
          <Route path="/help" element={<WithFooter><HelpCenter /></WithFooter>} />
          <Route path="/safety" element={<WithFooter><SafetyTrust /></WithFooter>} />
          <Route path="/contact" element={<WithFooter><ContactUs /></WithFooter>} />
          <Route path="/careers" element={<WithFooter><Careers /></WithFooter>} />
          <Route path="/professionals" element={<WithFooter><ForProfessionals /></WithFooter>} />


          {/* Protected Routes */}
          <Route element={<PrivateRoute />}>
            <Route element={<RoleProtectedRoute allowedRoles={['university', 'custom_role']} />}>
              <Route path="/uni-dashboard" element={<UniDashboard />} />
              <Route path="/stage-allocation" element={<Navigate to="/uni-dashboard?tab=stage_allocation" replace />} />
            </Route>

            <Route element={<RoleProtectedRoute allowedRoles={['pop']} />}>
              <Route path="/pop-dashboard" element={<PopDashboard />} />
              <Route path="/pop-lms" element={<PopLMSPage />} />
              <Route path="/pop-lms/:courseId" element={<PopLMSPage />} />
            </Route>

            <Route element={<RoleProtectedRoute allowedRoles={['admin', 'support', 'system_admin']} />}>
              <Route path="/admin-dashboard" element={<AdminDashboard />} />
              <Route path="/admin/reports" element={<AdminReportsPage />} />
              <Route path="/admin/messages" element={<AdminMessagesPage />} />
              <Route path="/admin/professor/:id" element={<ProfessorProfileView />} />
              <Route path="/admin/university/:id" element={<UniversityProfileView />} />
            </Route>

            <Route element={<RoleProtectedRoute allowedRoles={['student']} />}>
              <Route path="/student-dashboard" element={<StudentDashboard />} />
            </Route>

            <Route element={<RoleProtectedRoute allowedRoles={['faculty']} />}>
              <Route path="/faculty-dashboard" element={<FacultyDashboard />} />
            </Route>

            <Route element={<RoleProtectedRoute allowedRoles={['tpo']} />}>
              <Route path="/tpo-dashboard" element={<TpoDashboard />} />
            </Route>
            <Route path="/discover" element={<DiscoverProfessors />} />
            <Route path="/discoveruni" element={<DiscoverProfessorsUni />} />
            <Route path="/vetting-page" element={<FeatureRouteGuard feature="vetting"><VettingPage /></FeatureRouteGuard>} />
            <Route path="/compliance-dashboard" element={<FeatureRouteGuard feature="vetting"><ComplianceDashboard /></FeatureRouteGuard>} />
            <Route path="/chat" element={<FeatureRouteGuard feature="messages"><ChatPage /></FeatureRouteGuard>} />
            <Route path="/discussions" element={<FeatureRouteGuard feature="discussions"><DiscussionsPage /></FeatureRouteGuard>} />
            <Route path="/ai-agents" element={<FeatureRouteGuard feature="ai_agents"><AiAgents /></FeatureRouteGuard>} />
            <Route path="/ai-agents/resume-scorer" element={<FeatureRouteGuard feature="ai_agents" sub="ai_resume_scorer"><AiAgents /></FeatureRouteGuard>} />
            <Route path="/ai-agents/counsellor" element={<FeatureRouteGuard feature="ai_agents" sub="ai_counsellor"><AiAgents /></FeatureRouteGuard>} />
            <Route path="/ai-agents/interviewer" element={<FeatureRouteGuard feature="ai_agents" sub="ai_interviewer"><AiAgents /></FeatureRouteGuard>} />
            <Route path="/ai-agents/tutor" element={<FeatureRouteGuard feature="ai_agents" sub="ai_tutor"><AiAgents /></FeatureRouteGuard>} />
            <Route path="/ai-interviewer" element={<FeatureRouteGuard feature="sidebar_interviewer"><SidebarAiInterviewer /></FeatureRouteGuard>} />
            <Route path="/user-manual" element={<UserManualPage />} />
            <Route path="/service-desk" element={<ServiceDeskPage />} />
          </Route>
        </Routes>
      </div>
    </FeatureFlagProvider>
    </AppErrorBoundary>
    </Router>
  );
}

export default App;
